import { createAction } from '@reduxjs/toolkit'
import { reduxBatch } from '@manaflair/redux-batch'
import {
  downloadAction,
  printAction,
  skipGAEventAction,
  uploadAction,
} from '../../../../utils/actions'
import { doDownload } from '../../../../utils/actions'
import { formatEntries } from '../../../../utils/utils'
import { APP_CONFIG, CONNECTION_TYPE, CONNECTION_TYPE_LABEL } from '../../common/constants'
import { GAService } from '../../../../utils/services'
import { getJobTallyState, jobsActions, jobsAdapter, resultActions } from '../slices'
import { deepCopy, isUndefined, numberFormatted, toSingleDepthObject } from '../../../../utils/fns'
import { getJobInputs, selectMembersState } from '../selectors'
import { updateProductSpec } from '../../views/Output/utils/fastener'
import { getICILabel, prepareJobData } from '../../common/fns'
import merge from 'lodash/merge'
import omit from 'lodash/omit'
import { getCalcInputState, getCalcOutputState } from '../../../../utils/selectors'
import { ijoistUtils } from '../../common/utils/ijoistUtils'

const IJOIST_AFFECTED_MEMBER = ['joist', 'header', 'truss']

const generateGAInput = (appInput) => {
  const elements = document.querySelectorAll('[data-ga4]')
  let gaInput = {}
  switch (appInput.settings.connectionType) {
    case CONNECTION_TYPE.JOIST: {
      gaInput = {
        settings_hangerType: 'N/A',
        header_memberType: 'N/A',
        header_lumberSpecies: 'N/A',
        header_width: 'N/A',
        header_depth: 'N/A',
        header_ply: 'N/A',
        header_isRough: 'N/A',
        header_thickness: 'N/A',
        joist_memberType: 'N/A',
        joist_lumberSpecies: 'N/A',
        joist_width: 'N/A',
        joist_depth: 'N/A',
        joist_ply: 'N/A',
        joist_isRough: 'N/A',
        joist_angle_skewValue: 'N/A',
        joist_angle_skewType: 'N/A',
        joist_angle_slopeValue: 'N/A',
        joist_angle_slopeType: 'N/A',
      }
      break
    }
    case CONNECTION_TYPE.TRUSS: {
      gaInput = {
        settings_hangerType: 'N/A',
        girder_memberType: 'N/A',
        girder_lumberSpecies: 'N/A',
        girder_width: 'N/A',
        girder_depth: 'N/A',
        girder_ply: 'N/A',
        girder_kingWidth: 'N/A',
        girder_kingHeight: 'N/A',
        truss_memberType: 'N/A',
        truss_lumberSpecies: 'N/A',
        truss_width: 'N/A',
        truss_depth: 'N/A',
        truss_ply: 'N/A',
        truss_angle_skewValue: 'N/A',
        truss_angle_skewType: 'N/A',
        truss_angle_slopeValue: 'N/A',
        truss_angle_slopeType: 'N/A',
      }
      break
    }
    case CONNECTION_TYPE.MULTI_TRUSS: {
      gaInput = {
        settings_hangerType: 'N/A',
        girder_memberType: 'N/A',
        girder_lumberSpecies: 'N/A',
        girder_width: 'N/A',
        girder_depth: 'N/A',
        girder_ply: 'N/A',
        girder_kingWidth: 'N/A',
        jack_memberType: 'N/A',
        jack_width: 'N/A',
        jack_depth: 'N/A',
        jack_ply: 'N/A',
        jack_angle_slopeValue: 'N/A',
        leftHip_memberType: 'N/A',
        leftHip_width: 'N/A',
        leftHip_depth: 'N/A',
        leftHip_ply: 'N/A',
        leftHip_angle_skewValue: 'N/A',
        leftHip_angle_slopeValue: 'N/A',
        leftHip_angle_slopeType: 'N/A',
        rightHip_isDuplicate: 'N/A',
      }
      break
    }
  }

  elements.forEach((el) => {
    const ga4Data = el.dataset.ga4.split(':')
    if (gaInput[ga4Data[0]]) gaInput[ga4Data[0]] = ga4Data[1]
  })

  return {
    ...gaInput,
    settings_country: appInput.settings.country,
    settings_connectionType: CONNECTION_TYPE_LABEL[appInput.settings.connectionType],
  }
}

const generateGAOutput = (gaOutput) => {
  const {
    model,
    iciLabel,
    quantity,
    wSize,
    hSize,
    bSize,
    tfSize,
    tfFastenersLabel,
    fFastenersLabel,
    jFastenersLabel,
    load,
    uplift,
    updatedSpec,
    updatedWebStiffeners,
  } = gaOutput

  const formattedModelName = model + updatedWebStiffeners + (updatedSpec ? ' ' + updatedSpec : '')

  return {
    model: formattedModelName,
    ici: iciLabel,
    quantity: isUndefined(quantity) ? 'N/A' : quantity,
    width: isUndefined(wSize) ? 'N/A' : wSize,
    height: isUndefined(hSize) ? 'N/A' : hSize,
    bearing: isUndefined(bSize) ? 'N/A' : bSize,
    tfDepth: isUndefined(tfSize) ? 'N/A' : tfSize || '-',
    tfFasteners: isUndefined(tfFastenersLabel) ? 'N/A' : tfFastenersLabel,
    faceFasteners: isUndefined(fFastenersLabel) ? 'N/A' : fFastenersLabel,
    joistFasteners: isUndefined(jFastenersLabel) ? 'N/A' : jFastenersLabel,
    download: isUndefined(load) ? 'N/A' : load,
    upload: isUndefined(uplift) ? 'N/A' : uplift,
  }
}

const populateAppState = (store, input, output) => {
  const { jobs, ...uploadedData } = Object.assign({}, input, output)

  store.dispatch(jobsActions.jobCleared())

  reduxBatch(
    Object.keys(uploadedData)
      .map((key) => {
        const action = createAction(`${key}/updateCalculation`)
        return store.dispatch(action(uploadedData[key]))
      })
      .concat(
        Object.values(jobs.entities).map((value) => store.dispatch(jobsActions.jobAdded(value))),
      ),
  )
}

const convertLegacyMemberData = (member) => ({
  ...member,
  ijoistInputType: ijoistUtils.convertLegacyInputType(member.tabNumber),
  isRough: isUndefined(member.isRough) ? null : member.isRough,
  material: ijoistUtils.convertLegacyMaterial(member.type, member.material),
})

const flatteningJobInputs = (inputs) => {
  const result = {}

  for (const [key, value] of Object.entries(inputs)) {
    const flattened = toSingleDepthObject(value)

    switch (key) {
      case 'joist':
      case 'header':
      case 'truss':
        result[key] = convertLegacyMemberData(flattened)
        break
      case 'setting':
        result.settings = flattened
        break
      case 'hanger':
        result.hangerOptions = flattened
        break
      default:
        result[key] = flattened
        break
    }
  }

  return result
}

const convertLegacyJobEntities = (entities = []) =>
  entities.reduce((updatedJobEntities, jobEntity) => {
    const {
      tfFastenerDisplay,
      fFastenerDisplay,
      jFastenerDisplay,
      webStiffeners,
      config3d,
      spec,
      hlcFlush,
      hangerType,
      ici,
      ...rest
    } = jobEntity

    const jobData = {
      ...rest,
      tfFastenersLabel: tfFastenerDisplay,
      fFastenersLabel: fFastenerDisplay,
      jFastenersLabel: jFastenerDisplay,
      updatedSpec: updateProductSpec(spec, hlcFlush, hangerType),
      updatedWebStiffeners: webStiffeners ? '*' : '',
      iciLabel: getICILabel(ici),
      modelImg: config3d?.imageUrl,
    }

    const cleanedJobEntity = omit(jobEntity, [
      'tfFastenerDisplay',
      'fFastenerDisplay',
      'jFastenerDisplay',
      'tfFasteners',
      'fFasteners',
      'jFasteners',
      'config3d',
    ])

    const flattenedJobInputs = flatteningJobInputs(cleanedJobEntity.jobInputs)
    const finalJobData = prepareJobData(flattenedJobInputs, jobData)

    updatedJobEntities[jobEntity.uid] = finalJobData

    return updatedJobEntities
  }, {})

const convertLegacyStateStructure = (source) => {
  const inputState = getCalcInputState(source)
  const outputState = getCalcOutputState(source)
  const jobEntities = jobsAdapter.getSelectors(getJobTallyState).selectEntities(source)

  const formattedInput = {
    hangerOptions: toSingleDepthObject(inputState.hangerOptions),
    header: convertLegacyMemberData(toSingleDepthObject(inputState.member.header)),
    joist: convertLegacyMemberData(toSingleDepthObject(inputState.member.joist)),
    girder: toSingleDepthObject(inputState.member.girder),
    truss: toSingleDepthObject(inputState.member.truss),
    jack: toSingleDepthObject(inputState.member.jack),
    leftHip: toSingleDepthObject(inputState.member.leftHip),
    rightHip: toSingleDepthObject(inputState.member.rightHip),
    settings: toSingleDepthObject(inputState.setting),
    jobSettings: toSingleDepthObject(inputState.jobSettings),
  }

  return {
    input: formattedInput,
    output: {
      result: {
        optimized: true,
        searchKeyword: outputState.result.query ?? '',
      },
      jobs: {
        entities: convertLegacyJobEntities(jobEntities),
      },
    },
  }
}

const convertLegacyMaterialForIJoistMembers = (source) => {
  const memberState = selectMembersState(source)
  const jobEntities = jobsAdapter.getSelectors(getJobTallyState).selectAll(source)

  const convertedJobEntities = jobEntities.reduce((updatedJobEntities, job) => {
    const updatedJobInputs = { ...job.jobInputs }

    for (const [key, value] of Object.entries(updatedJobInputs)) {
      if (IJOIST_AFFECTED_MEMBER.includes(key)) {
        updatedJobInputs[key].material = ijoistUtils.convertLegacyMaterial(
          value.type,
          value.material,
        )
      }
    }

    updatedJobEntities[job.uid] = {
      ...job,
      jobInputs: updatedJobInputs,
    }

    return updatedJobEntities
  }, {})

  const convertedInputs = IJOIST_AFFECTED_MEMBER.reduce((updatedInputs, key) => {
    if (memberState[key]) {
      updatedInputs[key] = {
        ...memberState[key],
        material: ijoistUtils.convertLegacyMaterial(
          memberState[key].type,
          memberState[key].material,
        ),
      }
    }
    return updatedInputs
  }, {})

  return {
    input: convertedInputs,
    output: {
      jobs: {
        entities: convertedJobEntities,
      },
    },
  }
}

const VERSION_CHANGELOG = {
  STATE_RESTRUCTION: '1.2.0',
  IJOIST_ENUM: '1.5.0',
}

const transformUploadPayload = (source) => {
  let updatedCalculationState

  if (source?.appVersion < VERSION_CHANGELOG.STATE_RESTRUCTION) {
    updatedCalculationState = convertLegacyStateStructure(source)
  } else if (source?.appVersion < VERSION_CHANGELOG.IJOIST_ENUM) {
    updatedCalculationState = convertLegacyMaterialForIJoistMembers(source)
  }

  return merge({}, source, {
    calculation: updatedCalculationState,
  })
}

const gaEvents = (store) => (next) => (action) => {
  next(action)
  const {
    calculation: { input, output },
    ui,
  } = store.getState()

  const appEventName = GAService.getAppEventName(APP_CONFIG.appCode, ui.ecMode)
  const outputResult = Object.assign({}, output.result)

  switch (action.type) {
    case uploadAction.type: {
      const uploadSource = deepCopy(action.payload)
      const uploadAppState = transformUploadPayload(uploadSource)
      populateAppState(store, uploadAppState.calculation.input, uploadAppState.calculation.output)

      GAService.handleGAEvent({
        appEventName,
        gaActionName: GAService.APP_ACTION.UPLOAD,
        gaLabelName: 'Upload',
        gaInputData: input,
        gaOutputData: outputResult,
      })
      GAService.handleDataLayerEvent({
        appName: appEventName,
        appAction: GAService.APP_ACTION.UPLOAD,
        appInputs: generateGAInput(uploadAppState.calculation.input),
        appOutputData: outputResult,
      })
      break
    }
    case downloadAction.type: {
      const {
        result: { optimized, searchKeyword },
        jobs,
      } = output

      const clonedInput = deepCopy(input)

      const downloadContent = {
        appCode: APP_CONFIG.appCode,
        appVersion: APP_CONFIG.appVersion,
        calculation: {
          input: numberFormatted(clonedInput),
          output: {
            result: { optimized, searchKeyword },
            jobs,
          },
        },
      }
      doDownload(downloadContent, APP_CONFIG.downloadFileName)
      GAService.handleGAEvent({
        appEventName,
        gaActionName: GAService.APP_ACTION.DOWNLOAD,
        gaLabelName: 'Download',
        gaInputData: input,
        gaOutputData: outputResult,
      })
      GAService.handleDataLayerEvent({
        appName: appEventName,
        appAction: GAService.APP_ACTION.DOWNLOAD,
        appInputs: generateGAInput(input),
        appOutputData: outputResult,
      })
      break
    }
    case printAction.type: {
      GAService.handleGAEvent({
        appEventName,
        gaActionName: GAService.APP_ACTION.PRINT,
        gaLabelName: 'Print',
        gaInputData: input,
        gaOutputData: outputResult,
      })
      GAService.handleDataLayerEvent({
        appName: appEventName,
        appAction: GAService.APP_ACTION.PRINT,
        appInputs: generateGAInput(input),
        appOutputData: outputResult,
      })
      break
    }
    case resultActions.getHangersData.fulfilled.type:
    case resultActions.getHangersData.rejected.type: {
      if (ui.canSkipGAEvent) {
        // restore default value because we only skip once
        store.dispatch(skipGAEventAction(false))
        break
      }
      GAService.handleGAEvent({
        appEventName,
        gaActionName: GAService.APP_ACTION.CALCULATION,
        gaLabelName: 'Calculation',
        gaInputData: input,
        gaOutputData: outputResult,
      })
      GAService.handleDataLayerEvent({
        appName: appEventName,
        appAction: GAService.APP_ACTION.CALCULATION,
        appInputs: generateGAInput(input),
        appOutputData: outputResult,
      })
      break
    }
    case jobsActions.jobAdded.type: {
      const { id, jobInputs } = action.payload

      const eventLabel = formatEntries(jobInputs, `AddToJobList-${id}`)
      GAService.doGAEvent(appEventName, 'AddToJobList_Action', eventLabel)

      GAService.handleDataLayerEvent({
        appName: appEventName,
        appAction: 'AddToJobList_Action',
        gaOutputs: generateGAOutput(action.payload),
      })
      break
    }
    case resultActions.setSelectedRow.type: {
      if (action.payload) {
        const { id } = action.payload
        const jobInputs = getJobInputs(store.getState())
        const eventLabel = formatEntries(jobInputs, `ProductSelected-${id}`)
        GAService.doGAEvent(appEventName, 'Product_Selected_Action', eventLabel)

        const gaOutputPayload = {
          ...action.payload,
          quantity: jobInputs.jobSettings.quantity,
        }

        GAService.handleDataLayerEvent({
          appName: appEventName,
          appAction: 'Product_Selected_Action',
          gaOutputs: generateGAOutput(gaOutputPayload),
        })
      }
      break
    }
    case resultActions.productOrderAction.type: {
      const { payload } = action

      const gaOutput = generateGAOutput(payload)

      GAService.handleDataLayerEvent({
        appName: appEventName,
        appAction: 'Order_Action',
        gaOutputs: {
          job: payload.uid,
          model: gaOutput.model,
          quantity: gaOutput.quantity,
          tfFasteners: gaOutput.tfFasteners,
          faceFasteners: gaOutput.faceFasteners,
          joistFasteners: gaOutput.joistFasteners,
        },
      })
      break
    }
    default:
      break
  }
}

export default gaEvents
